import { useEffect, useMemo, useState, type ReactNode } from "react";
import { collection, doc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { Film, Image as ImageIcon, Megaphone, Play, Search, X } from "lucide-react";

import Header from "../components/HeaderV2";
import { db, storage } from "../firebaseConfig";
import { KioskType, useKiosk } from "../KioskContext";
import {
    CampaignDisplayStatus,
    CampaignMediaType,
    CampaignType,
    getCampaignDisplayStatus,
    useCampaign,
} from "../CampaignContext";
import common from "../style/screens/CommonV2.module.css";
import style from "../style/screens/CampaignV2.module.css";

const IMAGE_MAX_BYTES = 10 * 1024 * 1024;
const VIDEO_MAX_BYTES = 100 * 1024 * 1024;
const KST_OFFSET_MS = 9 * 60 * 60 * 1000;

const STATUS_LABELS: Record<CampaignDisplayStatus, string> = {
    DRAFT: "작성 중",
    SCHEDULED: "예약",
    ACTIVE: "송출 중",
    PAUSED: "일시정지",
    ENDED: "종료",
};

const KIOSK_STATUS_LABELS: Record<KioskType["status"], string> = {
    PENDING: "등록 대기",
    ACTIVE: "운영 중",
    FULL: "수거 필요",
    INACTIVE: "운영 중지",
};

const DELIVERY_LABELS: Record<string, string> = {
    QUEUED: "다운로드 대기",
    DOWNLOADING: "다운로드 중",
    APPLIED: "적용 완료",
    FAILED: "적용 실패",
};

const toKstInput = (timestamp: number | null) => {
    if (timestamp === null) return "";
    return new Date(timestamp + KST_OFFSET_MS).toISOString().slice(0, 16);
};

const fromKstInput = (value: string) => {
    if (!value) return null;
    const parsed = Date.parse(`${value}:00+09:00`);
    return Number.isFinite(parsed) ? parsed : null;
};

const formatDateTime = (timestamp: number | null) => {
    if (timestamp === null) return "제한 없음";
    return new Intl.DateTimeFormat("ko-KR", {
        timeZone: "Asia/Seoul",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(timestamp);
};

const formatBytes = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)}MB`;

const CampaignV2 = () => {
    const {
        campaigns,
        deliveries,
        loading,
        publishCampaign,
        pauseCampaign,
        resumeCampaign,
        hideCampaign,
        unhideCampaign,
        discardCampaignDraft,
    } = useCampaign();
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState<"ALL" | "HIDDEN" | CampaignDisplayStatus>("ALL");
    const [editing, setEditing] = useState<CampaignType | null | undefined>(undefined);
    const [detail, setDetail] = useState<CampaignType | null>(null);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 30_000);
        return () => window.clearInterval(timer);
    }, []);

    const visibleCampaigns = useMemo(
        () => campaigns.filter((campaign) => campaign.archivedAtMs === null || campaign.archivedAtMs === undefined),
        [campaigns],
    );
    const hiddenCampaigns = useMemo(
        () => campaigns.filter((campaign) => campaign.archivedAtMs !== null && campaign.archivedAtMs !== undefined),
        [campaigns],
    );

    const filtered = useMemo(() => campaigns.filter((campaign) => {
        const status = getCampaignDisplayStatus(campaign, now);
        const keyword = search.trim().toLowerCase();
        const hidden = campaign.archivedAtMs !== null && campaign.archivedAtMs !== undefined;
        const matchesStatus = statusFilter === "HIDDEN"
            ? hidden
            : !hidden && (statusFilter === "ALL" || status === statusFilter);
        return matchesStatus
            && (!keyword || campaign.title.toLowerCase().includes(keyword));
    }), [campaigns, now, search, statusFilter]);

    const deliverySummary = (campaign: CampaignType) => {
        const current = deliveries.filter((item) =>
            item.campaignId === campaign.id && item.campaignRevision === campaign.revision,
        );
        return {
            applied: current.filter((item) => item.status === "APPLIED").length,
            failed: current.filter((item) => item.status === "FAILED").length,
            total: campaign.targetKioskDocIds.length,
        };
    };

    const runAction = async (action: () => Promise<void>, successMessage: string) => {
        try {
            await action();
            setDetail(null);
            alert(successMessage);
        } catch (error: any) {
            console.error(error);
            alert(error?.message || "캠페인 상태를 변경하지 못했습니다.");
        }
    };

    const activeCount = visibleCampaigns.filter((item) => getCampaignDisplayStatus(item, now) === "ACTIVE").length;
    const scheduledCount = visibleCampaigns.filter((item) => getCampaignDisplayStatus(item, now) === "SCHEDULED").length;
    const pausedCount = visibleCampaigns.filter((item) => getCampaignDisplayStatus(item, now) === "PAUSED").length;

    return (
        <>
            <Header />
            <main className={common["container"]}>
                <div className={common["wrapper"]}>
                    <div className={style["title-row"]}>
                        <div>
                            <h4 className={common["text--title"]}>캠페인 관리</h4>
                            <p className={common["text--copy"]}>키오스크 대기 화면에 노출할 이미지와 영상을 관리합니다.</p>
                        </div>
                        <button className={style["button--primary"]} onClick={() => setEditing(null)}>
                            <Megaphone size={17} /> 캠페인 생성
                        </button>
                    </div>

                    <section className={style["summary-grid"]}>
                        <SummaryCard label="전체 캠페인" value={visibleCampaigns.length} />
                        <SummaryCard label="송출 중" value={activeCount} tone="active" />
                        <SummaryCard label="예약" value={scheduledCount} />
                        <SummaryCard label="일시정지" value={pausedCount} />
                    </section>

                    <section className={style["panel"]}>
                        <div className={style["toolbar"]}>
                            <label className={style["search"]}>
                                <Search size={17} />
                                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="캠페인 제목 검색" />
                            </label>
                            <div className={style["status-filters"]} role="group" aria-label="캠페인 상태 필터">
                                {([
                                    ["ALL", "전체 캠페인", visibleCampaigns.length],
                                    ["ACTIVE", "송출 중", activeCount],
                                    ["SCHEDULED", "예약", scheduledCount],
                                    ["PAUSED", "일시정지", pausedCount],
                                    ["HIDDEN", "숨김", hiddenCampaigns.length],
                                ] as const).map(([value, label, count]) => (
                                    <button
                                        key={value}
                                        type="button"
                                        className={[style["status-filter"], statusFilter === value ? style["status-filter--active"] : ""].join(" ")}
                                        aria-pressed={statusFilter === value}
                                        onClick={() => setStatusFilter(value)}
                                    >
                                        {label}<span>{count}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className={style["campaign-table"]}>
                            <div className={[style["campaign-row"], style["campaign-row--head"]].join(" ")}>
                                <span>캠페인</span><span>송출 기간</span><span>대상</span><span>배포</span><span>상태</span>
                            </div>
                            {loading && <div className={style["empty"]}>캠페인 정보를 불러오고 있습니다.</div>}
                            {!loading && filtered.length === 0 && <div className={style["empty"]}>조건에 맞는 캠페인이 없습니다.</div>}
                            {filtered.map((campaign) => {
                                const status = getCampaignDisplayStatus(campaign, now);
                                const summary = deliverySummary(campaign);
                                return (
                                    <button key={campaign.id} className={style["campaign-row"]} onClick={() => setDetail(campaign)}>
                                        <span className={style["campaign-name"]}>
                                            <CampaignThumbnail campaign={campaign} />
                                            <span><strong>{campaign.title}</strong><small>{campaign.mediaType === "IMAGE" ? `이미지 · ${campaign.imageDurationSec}초` : "동영상"}</small></span>
                                        </span>
                                        <span className={style["period"]}>{formatDateTime(campaign.startAtMs)}<small>~ {formatDateTime(campaign.endAtMs)}</small></span>
                                        <span>{campaign.targetKioskDocIds.length}대</span>
                                        <span className={summary.failed ? style["delivery-failed"] : ""}>{summary.applied}/{summary.total} 적용{summary.failed ? ` · 실패 ${summary.failed}` : ""}</span>
                                        <span><StatusBadge status={status} /></span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>
                </div>
            </main>

            {editing !== undefined && <CampaignEditor campaign={editing} close={() => setEditing(undefined)} />}
            {detail && (
                <CampaignDetail
                    campaign={detail}
                    close={() => setDetail(null)}
                    edit={() => { setDetail(null); setEditing(detail); }}
                    publish={() => runAction(() => publishCampaign(detail.id), "캠페인을 게시했습니다.")}
                    pause={() => runAction(() => pauseCampaign(detail.id), "캠페인을 일시정지했습니다.")}
                    resume={() => runAction(() => resumeCampaign(detail.id), "캠페인 송출을 재개했습니다.")}
                    hide={() => runAction(() => hideCampaign(detail.id), "캠페인을 숨겼습니다.")}
                    unhide={() => runAction(() => unhideCampaign(detail.id), "캠페인 숨기기를 해제했습니다.")}
                    discard={() => runAction(() => discardCampaignDraft(detail.id), "초안을 폐기했습니다.")}
                />
            )}
        </>
    );
};

const SummaryCard = ({ label, value, tone }: { label: string; value: number; tone?: string }) => (
    <article className={[style["summary-card"], tone && style[`summary-card--${tone}`]].filter(Boolean).join(" ")}>
        <span>{label}</span><strong>{value.toLocaleString()}</strong>
    </article>
);

const StatusBadge = ({ status }: { status: CampaignDisplayStatus }) => (
    <span className={[style["status"], style[`status--${status.toLowerCase()}`]].join(" ")}>{STATUS_LABELS[status]}</span>
);

const CampaignThumbnail = ({ campaign }: { campaign: CampaignType }) => {
    const [url, setUrl] = useState("");
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let active = true;
        setUrl("");
        setFailed(false);
        getDownloadURL(ref(storage, campaign.storagePath))
            .then((downloadUrl) => { if (active) setUrl(downloadUrl); })
            .catch(() => { if (active) setFailed(true); });
        return () => { active = false; };
    }, [campaign.assetVersion, campaign.storagePath]);

    if (!url || failed) {
        return (
            <span className={style["media-icon"]} aria-hidden="true">
                {campaign.mediaType === "IMAGE" ? <ImageIcon size={18} /> : <Film size={18} />}
            </span>
        );
    }

    return (
        <span className={style["campaign-thumbnail"]} aria-hidden="true">
            {campaign.mediaType === "IMAGE" ? (
                <img src={url} alt="" onError={() => setFailed(true)} />
            ) : (
                <>
                    <video
                        src={url}
                        muted
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={(event) => {
                            const video = event.currentTarget;
                            video.currentTime = Number.isFinite(video.duration)
                                ? Math.min(1, Math.max(0.1, video.duration * 0.05))
                                : 0.1;
                        }}
                        onError={() => setFailed(true)}
                    />
                    <span className={style["campaign-thumbnail__play"]}><Play size={12} fill="currentColor" /></span>
                </>
            )}
        </span>
    );
};

const CampaignEditor = ({ campaign, close }: { campaign: CampaignType | null; close: () => void }) => {
    const { kiosks, pendings } = useKiosk();
    const { saveCampaign, publishCampaign } = useCampaign();
    const allKiosks = useMemo(() => {
        const map = new Map<string, KioskType>();
        [...pendings, ...kiosks].forEach((item) => map.set(item.docId, item));
        return Array.from(map.values());
    }, [kiosks, pendings]);
    const [title, setTitle] = useState(campaign?.title || "");
    const [mediaType, setMediaType] = useState<CampaignMediaType>(campaign?.mediaType || "IMAGE");
    const [imageDuration, setImageDuration] = useState(String(campaign?.imageDurationSec || 10));
    const [startAt, setStartAt] = useState(toKstInput(campaign?.startAtMs ?? null));
    const [endAt, setEndAt] = useState(toKstInput(campaign?.endAtMs ?? null));
    const [targets, setTargets] = useState<string[]>(campaign?.targetKioskDocIds || []);
    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [search, setSearch] = useState("");
    const [partnerFilter, setPartnerFilter] = useState("ALL");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [targetSort, setTargetSort] = useState<"PARTNER" | "NAME">("PARTNER");
    const [saving, setSaving] = useState(false);
    const [progress, setProgress] = useState(0);

    const ended = campaign ? getCampaignDisplayStatus(campaign) === "ENDED" : false;

    useEffect(() => {
        if (!campaign || file) return;
        let active = true;
        getDownloadURL(ref(storage, campaign.storagePath))
            .then((url) => { if (active) setPreviewUrl(url); })
            .catch((error) => console.error("캠페인 미리보기 URL 오류", error));
        return () => { active = false; };
    }, [campaign, file]);

    useEffect(() => () => {
        if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    }, [previewUrl]);

    const partners = (Array.from(new Set(allKiosks.map((item) => item.partnerId).filter(Boolean))) as string[])
        .sort((a, b) => a.localeCompare(b, "ko"));
    const filteredKiosks = allKiosks
        .filter((item) => {
            const keyword = search.toLowerCase();
            return (partnerFilter === "ALL" || item.partnerId === partnerFilter)
                && (statusFilter === "ALL" || item.status === statusFilter)
                && (!keyword || [item.name, item.id, item.address].some((value) => value?.toLowerCase().includes(keyword)));
        })
        .sort((a, b) => {
            const nameCompare = (a.name || a.id).localeCompare(b.name || b.id, "ko");
            if (targetSort === "NAME") return nameCompare;
            return (a.partnerId || "").localeCompare(b.partnerId || "", "ko") || nameCompare;
        });

    const chooseFile = (nextFile?: File) => {
        if (!nextFile) return;
        const nextType: CampaignMediaType = nextFile.type === "video/mp4" ? "VIDEO" : "IMAGE";
        const allowed = nextType === "IMAGE"
            ? ["image/jpeg", "image/png"].includes(nextFile.type) && nextFile.size <= IMAGE_MAX_BYTES
            : nextFile.type === "video/mp4" && nextFile.size <= VIDEO_MAX_BYTES;
        if (!allowed) {
            alert(nextType === "IMAGE" ? "JPG/PNG 10MB 이하 파일만 사용할 수 있습니다." : "MP4 100MB 이하 파일만 사용할 수 있습니다.");
            return;
        }
        if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
        setMediaType(nextType);
        setFile(nextFile);
        setPreviewUrl(URL.createObjectURL(nextFile));
    };

    const validate = () => {
        if (!title.trim() || title.trim().length > 100) return "제목을 1~100자로 입력해주세요.";
        if (!campaign && !file) return "이미지 또는 동영상을 선택해주세요.";
        if (mediaType === "IMAGE" && (!Number.isInteger(Number(imageDuration)) || Number(imageDuration) < 10)) return "이미지 노출시간은 10초 이상이어야 합니다.";
        if (targets.length === 0) return "노출할 키오스크를 한 대 이상 선택해주세요.";
        const start = fromKstInput(startAt);
        const end = fromKstInput(endAt);
        if (startAt && start === null) return "시작일시를 확인해주세요.";
        if (endAt && end === null) return "종료일시를 확인해주세요.";
        if (start !== null && end !== null && start >= end) return "종료일시는 시작일시보다 늦어야 합니다.";
        return null;
    };

    const upload = (campaignId: string, assetVersion: number, source: File) => new Promise<string>((resolve, reject) => {
        const safeName = source.name.replace(/[^a-zA-Z0-9._-]/g, "_") || (mediaType === "VIDEO" ? "video.mp4" : "image.png");
        const storagePath = `campaign/${campaignId}/${assetVersion}/${Date.now()}_${safeName}`;
        const task = uploadBytesResumable(ref(storage, storagePath), source, { contentType: source.type });
        task.on("state_changed", (snapshot) => {
            setProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100));
        }, reject, () => resolve(storagePath));
    });

    const save = async (publish: boolean) => {
        const validationError = validate();
        if (validationError) { alert(validationError); return; }
        if (ended || saving) return;

        setSaving(true);
        let uploadedPath: string | null = null;
        let documentSaved = false;
        try {
            const campaignId = campaign?.id || doc(collection(db, "campaigns")).id;
            const assetVersion = file ? Number(campaign?.assetVersion || 0) + 1 : Number(campaign?.assetVersion || 1);
            const storagePath = file ? await upload(campaignId, assetVersion, file) : campaign!.storagePath;
            uploadedPath = file ? storagePath : null;
            await saveCampaign({
                campaignId,
                title: title.trim(),
                mediaType,
                storagePath,
                fileName: file?.name || campaign!.fileName,
                assetVersion,
                imageDurationSec: mediaType === "IMAGE" ? Number(imageDuration) : null,
                targetKioskDocIds: targets,
                startAtMs: fromKstInput(startAt),
                endAtMs: fromKstInput(endAt),
            });
            documentSaved = true;
            if (publish && (!campaign || campaign.publishState === "DRAFT")) {
                try {
                    await publishCampaign(campaignId);
                } catch (error) {
                    console.error(error);
                    alert("캠페인은 초안으로 저장됐지만 게시하지 못했습니다. 목록에서 다시 게시해주세요.");
                    close();
                    return;
                }
            }
            alert(publish ? "캠페인을 게시했습니다." : "캠페인을 저장했습니다.");
            close();
        } catch (error: any) {
            console.error(error);
            const code = String(error?.code || "");
            const definitelyRejectedBeforeSave = ["invalid-argument", "not-found", "permission-denied"]
                .some((value) => code.includes(value));
            if (uploadedPath && !documentSaved && definitelyRejectedBeforeSave) {
                await deleteObject(ref(storage, uploadedPath)).catch(() => {});
            }
            alert(error?.message || "캠페인을 저장하지 못했습니다.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={style["overlay"]}>
            <section className={style["editor"]}>
                <header className={style["modal-header"]}>
                    <div><h4>{campaign ? "캠페인 수정" : "새 캠페인 생성"}</h4></div>
                    <button onClick={close} aria-label="닫기" disabled={saving}><X size={20} /></button>
                </header>
                <div className={style["editor-body"]}>
                    <div className={style["campaign-workspace"]}>
                        <aside className={style["campaign-content-column"]}>
                            <FormSection number="1" title="콘텐츠">
                                <div className={style["content-stack"]}>
                                    <label className={style["upload-box"]}><input type="file" accept="image/jpeg,image/png,video/mp4" onChange={(event) => chooseFile(event.target.files?.[0])} disabled={ended} /><strong>JPG·PNG 또는 MP4 선택</strong><span>이미지 10MB · 동영상 100MB 이하</span></label>
                                    <CampaignMediaPreview url={previewUrl} mediaType={mediaType} />
                                    {mediaType === "IMAGE" && <label className={style["field"]}><span>이미지 노출시간(초)</span><input type="number" min={10} value={imageDuration} onChange={(event) => setImageDuration(event.target.value)} disabled={ended} /></label>}
                                </div>
                            </FormSection>
                        </aside>
                        <div className={style["campaign-form-column"]}>
                            <FormSection number="2" title="기본정보">
                                <label className={style["field"]}><span>캠페인 제목</span><input maxLength={100} value={title} onChange={(event) => setTitle(event.target.value)} disabled={ended} /></label>
                            </FormSection>
                            <FormSection number="3" title="송출기간">
                                <div className={style["two-columns"]}>
                                    <label className={style["field"]}><span>시작일시 · 비우면 즉시</span><input type="datetime-local" value={startAt} onChange={(event) => setStartAt(event.target.value)} disabled={ended} /></label>
                                    <label className={style["field"]}><span>종료일시 · 비우면 무기한</span><input type="datetime-local" value={endAt} onChange={(event) => setEndAt(event.target.value)} disabled={ended} /></label>
                                </div>
                            </FormSection>
                            <FormSection number="4" title={`노출 키오스크 · ${targets.length}대 선택`}>
                        <div className={style["target-toolbar"]}>
                            <input placeholder="이름·ID·주소 검색" value={search} onChange={(event) => setSearch(event.target.value)} />
                            <select value={partnerFilter} onChange={(event) => setPartnerFilter(event.target.value)}><option value="ALL">전체 파트너</option>{partners.map((partner) => <option key={partner}>{partner}</option>)}</select>
                            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="ALL">전체 상태</option>{(["PENDING", "ACTIVE", "FULL", "INACTIVE"] as KioskType["status"][]).map((status) => <option key={status} value={status}>{KIOSK_STATUS_LABELS[status]}</option>)}</select>
                            <div className={style["target-sort"]} role="group" aria-label="키오스크 정렬">
                                <button type="button" className={targetSort === "PARTNER" ? style["target-sort--active"] : ""} onClick={() => setTargetSort("PARTNER")}>파트너순</button>
                                <button type="button" className={targetSort === "NAME" ? style["target-sort--active"] : ""} onClick={() => setTargetSort("NAME")}>이름순</button>
                            </div>
                        </div>
                        <div className={style["target-list"]}>
                            <div className={style["target-list__head"]}><span /><span>키오스크</span><span>파트너</span><span>상태</span><span>주소</span></div>
                            {filteredKiosks.map((kiosk) => (
                                <label key={kiosk.docId} className={targets.includes(kiosk.docId) ? style["target--selected"] : ""}>
                                    <input type="checkbox" checked={targets.includes(kiosk.docId)} disabled={ended} onChange={() => setTargets((current) => current.includes(kiosk.docId) ? current.filter((id) => id !== kiosk.docId) : [...current, kiosk.docId])} />
                                    <span><strong>{kiosk.name || kiosk.id}</strong><small>{kiosk.id}</small></span>
                                    <span className={style["target-partner"]}>{kiosk.partnerId || "-"}</span>
                                    <span className={style["target-status"]}>{KIOSK_STATUS_LABELS[kiosk.status]}</span>
                                    <span className={style["target-address"]} title={kiosk.address || ""}>{kiosk.address || "-"}</span>
                                </label>
                            ))}
                            {filteredKiosks.length === 0 && <div className={style["empty"]}>검색 결과가 없습니다.</div>}
                        </div>
                            </FormSection>
                        </div>
                    </div>
                </div>
                <footer className={style["modal-footer"]}>
                    <span>{saving && file ? `업로드 ${progress}%` : "모든 일시는 한국시간 기준입니다."}</span>
                    <div><button className={style["button--secondary"]} onClick={close} disabled={saving}>취소</button><button className={style["button--secondary"]} onClick={() => save(false)} disabled={saving || ended}>{saving ? "저장 중" : "저장"}</button>{(!campaign || campaign.publishState === "DRAFT") && <button className={style["button--primary"]} onClick={() => save(true)} disabled={saving || ended}>저장 후 게시</button>}</div>
                </footer>
            </section>
        </div>
    );
};

const FormSection = ({ number, title, children }: { number: string; title: string; children: ReactNode }) => (
    <section className={style["form-section"]}><h5><span>{number}</span>{title}</h5>{children}</section>
);

const CampaignMediaPreview = ({ url, mediaType, error = false }: { url: string; mediaType: CampaignMediaType; error?: boolean }) => (
    <div className={style["preview-panel"]}>
        <div className={style["preview"]}>
            {url ? (
                mediaType === "IMAGE"
                    ? <img src={url} alt="캠페인 미리보기" />
                    : <video src={url} controls muted playsInline />
            ) : <span>{error ? "미리보기를 불러오지 못했습니다" : "미리보기"}</span>}
            {url && <div className={style["preview-touch-hint"]}>화면을 터치해 키오스크 이용하기</div>}
        </div>
        <small className={style["preview-caption"]}>키오스크 화면 미리보기</small>
        <small className={style["preview-caption"]}>권장사이즈 1080x1920px</small>
    </div>
);

const CampaignDetail = ({ campaign, close, edit, publish, pause, resume, hide, unhide, discard }: { campaign: CampaignType; close: () => void; edit: () => void; publish: () => void; pause: () => void; resume: () => void; hide: () => void; unhide: () => void; discard: () => void }) => {
    const { deliveries } = useCampaign();
    const { kiosks, pendings } = useKiosk();
    const kioskMap = new Map([...kiosks, ...pendings].map((item) => [item.docId, item]));
    const status = getCampaignDisplayStatus(campaign);
    const hidden = campaign.archivedAtMs !== null && campaign.archivedAtMs !== undefined;
    const currentDeliveries = deliveries.filter((item) => item.campaignId === campaign.id && item.campaignRevision === campaign.revision);
    const deliveryMap = new Map(currentDeliveries.map((item) => [item.kioskDocId, item]));
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewError, setPreviewError] = useState(false);

    useEffect(() => {
        let active = true;
        setPreviewUrl("");
        setPreviewError(false);
        getDownloadURL(ref(storage, campaign.storagePath))
            .then((url) => { if (active) setPreviewUrl(url); })
            .catch((error) => {
                console.error("캠페인 상세 미리보기 URL 오류", error);
                if (active) setPreviewError(true);
            });
        return () => { active = false; };
    }, [campaign.storagePath]);

    return (
        <div className={style["overlay"]}>
            <section className={[style["editor"], style["detail"]].join(" ")}>
                <header className={style["modal-header"]}><div><small>캠페인 상세</small><h4>{campaign.title}</h4></div><button onClick={close}><X size={20} /></button></header>
                <div className={style["editor-body"]}>
                    <div className={style["detail-workspace"]}>
                        <section className={[style["form-section"], style["detail-preview"]].join(" ")}>
                            <h5>업로드 콘텐츠</h5>
                            <CampaignMediaPreview url={previewUrl} mediaType={campaign.mediaType} error={previewError} />
                            {previewUrl && <a className={style["preview-link"]} href={previewUrl} target="_blank" rel="noreferrer">원본 파일 새 창에서 보기</a>}
                        </section>
                        <div className={style["detail-info-column"]}>
                            <div className={style["detail-grid"]}>
                                <Info label="상태"><StatusBadge status={status} /></Info>
                                <Info label="미디어">{campaign.mediaType === "IMAGE" ? `이미지 · ${campaign.imageDurationSec}초` : "동영상"}</Info>
                                <Info label="파일">{campaign.fileName} · {formatBytes(campaign.sizeBytes)}</Info>
                                <Info label="송출기간">{formatDateTime(campaign.startAtMs)} ~ {formatDateTime(campaign.endAtMs)}</Info>
                            </div>
                            <section className={style["form-section"]}><h5>키오스크 배포 상태</h5><div className={style["delivery-list"]}>
                                {campaign.targetKioskDocIds.map((id) => {
                                    const kiosk = kioskMap.get(id);
                                    const delivery = deliveryMap.get(id);
                                    return <div key={id}><span><strong>{kiosk?.name || kiosk?.id || id}</strong><small>{kiosk?.id || id}</small></span><span className={delivery?.status === "FAILED" ? style["delivery-failed"] : ""}>{DELIVERY_LABELS[delivery?.status || "QUEUED"]}{delivery?.errorCode ? ` · ${delivery.errorCode}` : ""}<small>{delivery?.updatedAtMs ? formatDateTime(delivery.updatedAtMs) : "-"}</small></span></div>;
                                })}
                            </div></section>
                        </div>
                    </div>
                </div>
                <footer className={style["modal-footer"]}><span /><div><button className={style["button--secondary"]} onClick={close}>닫기</button>{hidden && <button className={style["button--primary"]} onClick={unhide}>숨기기 해제</button>}{!hidden && status !== "ENDED" && <button className={style["button--secondary"]} onClick={edit}>수정</button>}{!hidden && status === "DRAFT" && <button className={style["button--danger"]} onClick={() => window.confirm("초안을 폐기할까요?") && discard()}>초안 폐기</button>}{!hidden && status === "DRAFT" && <button className={style["button--primary"]} onClick={publish}>게시</button>}{!hidden && ["ACTIVE", "SCHEDULED"].includes(status) && <button className={style["button--danger"]} onClick={pause}>일시정지</button>}{!hidden && ["PAUSED", "ENDED"].includes(status) && <button className={style["button--secondary"]} onClick={hide}>숨기기</button>}{!hidden && status === "PAUSED" && <button className={style["button--primary"]} onClick={resume}>송출 재개</button>}</div></footer>
            </section>
        </div>
    );
};

const Info = ({ label, children }: { label: string; children: ReactNode }) => <div className={style["info"]}><span>{label}</span><strong>{children}</strong></div>;

export default CampaignV2;
